import _ from 'lodash'
import styled, { css } from 'styled-components'
import { useEffect, useRef, useState } from 'react'

import { useViewportSize } from '../../hooks/useViewportSize'
import { Flex } from '../Flex'
import { Image } from '../Image'
import { ThemeWrapper, breakpoint, themeColor } from '../theme'
import packageInfo from '../../../package.json'
import { ContactWidget } from '../ContactWidget/ContactWidget'

const StyledFooter = styled(Flex)`
  position: relative;
  padding: ${({ theme }) => `${theme.space[4]} 100px`};
  color: ${themeColor('gray')};

  flex-direction: column;
  align-items: center;

  ${breakpoint.tablet(css`
    align-items: flex-start;
  `)}
`

const Brand = styled(Image)`
  height: 30px;
  margin-bottom: ${({ theme }) => theme.space[2]};
`
Brand.displayName = 'Brand'

const FooterBody = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column-reverse;
  width: 100%;

  ${breakpoint.tablet(css`
    flex-direction: row;
  `)}
`

const FooterText = styled.div`
  margin: 12px 0;
  line-height: initial;
  font-size: 12px;

  p {
    color: white !important;
  }
`

const FooterLink = styled.a`
  display: block;
  color: white;
  font-weight: bold;
  text-align: center;
  line-height: 18px;
  padding: 14px 0;

  ${breakpoint.tablet(css`
    text-align: right;
    margin-bottom: 0;
  `)}
`

export interface FooterProps {
  footerCmsHtmlContent?: string
  links: { url: string; text: string }[]
  logoUrl: {
    url: string
    alt?: string
  }
  logoMobileUrl: {
    url: string
    alt?: string
  }
  contactWidget:
    | {
        id: string
        title: string
        description: string
        link?: {
          url: string
          text: string
          target?: string
        }
        textColor: string
        image?: {
          url: string
          alt?: string
        }
        backgroundColor?: string
        icon?: string
      }
    | undefined
}

export const Footer = ({
  footerCmsHtmlContent,
  links,
  logoUrl,
  logoMobileUrl,
  contactWidget: contactWidgetBannerDefinition
}: FooterProps) => {
  const viewPort = useViewportSize()
  const contactWidgetExists = !!contactWidgetBannerDefinition
  const contactWidgetAdjustedToMobilePreferences =
    contactWidgetBannerDefinition && viewPort?.isMobile
      ? {
          ...contactWidgetBannerDefinition,
          icon: contactWidgetBannerDefinition.icon
            ? _.replace(contactWidgetBannerDefinition.icon, 'top', 'bottom') // for mobile resolutions always display contact widget on bottom
            : contactWidgetBannerDefinition.icon
        }
      : contactWidgetBannerDefinition
  const footerRef = useRef<HTMLElement>(null)
  const currentFooterRef = footerRef?.current
  const [footerLoaded, setFooterLoaded] = useState<boolean>(false)
  const [anchorContactWidget, setAnchorContactWidget] = useState<boolean>(true)
  useEffect(() => {
    if (!contactWidgetExists || !footerLoaded || !currentFooterRef) {
      return
    }

    const observer = new IntersectionObserver(entries => {
      const isInViewport = _.some(entries, entry => entry.isIntersecting)
      setAnchorContactWidget(isInViewport)
    })

    observer.observe(currentFooterRef)

    // Cleanup the observer when the component unmounts
    return () => observer.unobserve(currentFooterRef)
  }, [footerLoaded, contactWidgetExists, currentFooterRef])

  return (
    <ThemeWrapper>
      <StyledFooter ref={footerRef} bg="primaryDark" onLoad={() => setFooterLoaded(true)}>
        {contactWidgetAdjustedToMobilePreferences &&
          !_.includes(contactWidgetAdjustedToMobilePreferences?.icon, 'top') && (
            <ContactWidget
              contactWidget={contactWidgetAdjustedToMobilePreferences}
              anchor={anchorContactWidget && !viewPort?.isMobile}
            />
          )}
        <Brand
          src={viewPort?.isMobile || viewPort?.isTablet ? logoMobileUrl.url : logoUrl.url}
          alt={viewPort?.isMobile || viewPort?.isTablet ? logoMobileUrl.alt : logoUrl.alt}
        />

        <FooterBody>
          <FooterText className="rich-text-content" dangerouslySetInnerHTML={{ __html: footerCmsHtmlContent || '' }} />
          <Flex mb={2} flexDirection="column">
            {_.map(links, (l, i) => (
              <FooterLink key={i} href={l.url}>
                {l.text}
              </FooterLink>
            ))}
          </Flex>
          <div style={{ display: 'none' }}>briq core version: {packageInfo.version}</div>
        </FooterBody>
      </StyledFooter>
    </ThemeWrapper>
  )
}
